import { Trans, useLingui } from "@lingui/react/macro";
import clsx from "clsx";
import { X } from "lucide-react";
import { observer } from "mobx-react-lite";
import React, { useId } from "react";

import { assert, resolveNestedProperty } from "../../../utils/utils";
import { Button } from "../button";
import { FormFieldIdProvider, formFieldLabelId } from "./form-field-id-context";
import { FormModel } from "./form-model";
import styles from "./form-row.module.css";

export const FormRowEditable = observer(function FormRowEditable({
  form,
  label,
  attr,
  model,
  viewMode,
  editMode,
  editable,
  getEditedField,
  onChange,
  onEditClick,
}: {
  form?: FormModel;
  label: string;
  attr?: string;
  model?: Record<string, any>;
  viewMode: React.ReactNode;
  editMode?: (editedField: Record<string, any>) => React.ReactNode;
  editable?: boolean;
  getEditedField?: () => Record<string, any>;
  onChange?: (editedField: Record<string, any>) => void;
  /** Custom edit click handler. When provided, clicking Edit calls this instead of inline editing. */
  onEditClick?: () => void;
}) {
  const { t } = useLingui();
  const rowId = useId();
  const fieldId = useId();
  const editing = form?.editedId === rowId;

  assert(
    !editing || form?.editedField,
    "Expected edited field when in edit mode",
  );

  const handleEditClick = () => {
    if (onEditClick) {
      onEditClick();
    } else if (form) {
      assert(model && attr, "Model or attr is missing");
      const editedField = getEditedField
        ? getEditedField()
        : {
            [attr]: resolveNestedProperty(model, attr),
          };
      form.startEditing(rowId, editedField);
    }
  };

  return (
    <>
      <div className={styles.leftColumn}>
        <label id={formFieldLabelId(fieldId)} htmlFor={fieldId}>
          {label}:
        </label>
      </div>

      <FormFieldIdProvider value={fieldId}>
        <div className={clsx(styles.rightColumn, styles.columnMaxWidth)}>
          {!editing && viewMode}
          {editing && form?.editedField && editMode?.(form.editedField)}
        </div>
      </FormFieldIdProvider>

      <div className={styles.editColumn}>
        {!editing && editable !== false && (
          <Button variant="tertiary" size="sm" onClick={handleEditClick}>
            <Trans>Edit</Trans>
          </Button>
        )}

        {editing && (
          <>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => {
                assert(form.editedField, "editedField is missing");
                onChange?.(form.editedField);
                form.stopEditing();
              }}
            >
              <Trans>Update</Trans>
            </Button>{" "}
            <Button
              variant="tertiary"
              size="sm"
              icon={X}
              label={t`Cancel`}
              onClick={() => {
                form.stopEditing();
              }}
            />
          </>
        )}
      </div>
    </>
  );
});
